import { useRef, useCallback } from 'react';

const MOTION_THRESHOLD = 30;      // Pixel difference threshold (0-255)
const MOTION_PIXEL_RATIO = 0.02;  // 2% of pixels must change to trigger
const SAMPLE_INTERVAL = 500;      // ms between frame comparisons
const CANVAS_SCALE = 0.25;        // Downsample for performance

export function useMotionDetection({ onMotionStart, onMotionEnd }) {
  const canvasRef = useRef(null);
  const prevDataRef = useRef(null);
  const intervalRef = useRef(null);
  const isMotionRef = useRef(false);
  const cooldownRef = useRef(null);

  const start = useCallback((videoElement) => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    intervalRef.current = setInterval(() => {
      if (!videoElement || videoElement.readyState < 2) return;

      const w = Math.floor(videoElement.videoWidth * CANVAS_SCALE);
      const h = Math.floor(videoElement.videoHeight * CANVAS_SCALE);

      if (w === 0 || h === 0) return;

      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(videoElement, 0, 0, w, h);

      const current = ctx.getImageData(0, 0, w, h).data;

      if (prevDataRef.current) {
        const prev = prevDataRef.current;
        let changedPixels = 0;
        const totalPixels = w * h;

        for (let i = 0; i < current.length; i += 4) {
          const diff =
            Math.abs(current[i] - prev[i]) +
            Math.abs(current[i + 1] - prev[i + 1]) +
            Math.abs(current[i + 2] - prev[i + 2]);

          if (diff > MOTION_THRESHOLD * 3) changedPixels++;
        }

        const ratio = changedPixels / totalPixels;

        if (ratio > MOTION_PIXEL_RATIO) {
          if (!isMotionRef.current) {
            isMotionRef.current = true;
            onMotionStart?.();
          }
          // Reset cooldown
          if (cooldownRef.current) clearTimeout(cooldownRef.current);
          cooldownRef.current = setTimeout(() => {
            isMotionRef.current = false;
            onMotionEnd?.();
          }, 5000); // 5s no motion → end event
        }
      }

      prevDataRef.current = new Uint8ClampedArray(current);
    }, SAMPLE_INTERVAL);
  }, [onMotionStart, onMotionEnd]);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (cooldownRef.current) clearTimeout(cooldownRef.current);
    prevDataRef.current = null;
    isMotionRef.current = false;
  }, []);

  return { start, stop };
}
